import type { AppearanceMode } from './desktop-api';

export type AccentColors = Record<AppearanceMode, string>;
export const ORIGINAL_ACCENT_COLORS: AccentColors = { light: '#a94716', dark: '#da7b32' };
// Dark defaults to Sky, the brand blue of the website and videos (owner, 2026-10-05); it was Ice (#d2ecfe).
export const DEFAULT_ACCENT_COLORS: AccentColors = { light: '#004766', dark: '#79bef2' };
export const ACCENT_PRESETS = {
  light: [
    { name: 'Ocean', color: '#004766' },
    { name: 'Black', color: '#000000' },
    { name: 'Orange', color: '#a94716' },
    { name: 'Teal', color: '#08776f' },
    { name: 'Berry', color: '#a43d68' },
  ],
  dark: [
    { name: 'Sky', color: '#79bef2' },
    { name: 'Ice', color: '#d2ecfe' },
    { name: 'White', color: '#ffffff' },
    { name: 'Orange', color: '#da7b32' },
    { name: 'Yellow', color: '#e6ff29' },
    { name: 'Pink', color: '#f28eac' },
  ],
} as const;
export const isAccentColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
export const isAccentColors = (value: unknown): value is AccentColors =>
  !!value &&
  typeof value === 'object' &&
  isAccentColor((value as AccentColors).light) &&
  isAccentColor((value as AccentColors).dark);

const rgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const hex = (values: number[]) =>
  `#${values.map((n) => Math.round(n).toString(16).padStart(2, '0')).join('')}`;
const luminance = (color: string) =>
  rgb(color)
    .map((n) => {
      const c = n / 255;
      return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    })
    .reduce((sum, n, i) => sum + n * [0.2126, 0.7152, 0.0722][i], 0);
export const accentContrast = (a: string, b: string) => {
  const x = luminance(a),
    y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
};
// Keep the chosen hue, adjusting only its lightness when text would disappear.
export function readableAccent(color: string, mode: AppearanceMode): string {
  const backdrop = mode === 'light' ? '#ededed' : '#343434';
  const target = mode === 'light' ? 0 : 255;
  for (let step = 0; step <= 100; step++) {
    const candidate = hex(rgb(color).map((n) => n + ((target - n) * step) / 100));
    if (accentContrast(candidate, backdrop) >= 4.5) return candidate;
  }
  return mode === 'light' ? '#000000' : '#ffffff';
}

export function accentTokens(colors: AccentColors, mode: AppearanceMode): Record<string, string> {
  const chosen = colors[mode];
  const ink = readableAccent(chosen, mode);
  const onInk = accentContrast(ink, '#ffffff') >= 4.5 ? '#ffffff' : '#111111';
  // Neutral primary buttons and semantic warnings/errors deliberately stay neutral/semantic.
  return {
    '--cp-text-action': ink,
    '--cp-text-action-strong': ink,
    '--cp-border-action': ink,
    '--cp-action-bright': ink,
    '--cp-text-on-bright': onInk,
    '--cp-orange': chosen,
    '--cp-focus-ring': ink,
    '--cp-field-evidence-line': readableAccent(chosen, 'light'),
    '--cp-field-evidence-fill': `${chosen}40`,
    '--cp-text-selection-fill': `${chosen}70`,
    '--cp-intro-accent': ink,
    '--cp-intro-accent-hover': ink,
    '--cp-intro-on-accent': onInk,
    '--cp-preview-light-active': readableAccent(colors.light, 'light'),
    '--cp-preview-dark-active': readableAccent(colors.dark, 'dark'),
  };
}

export function applyAccentColors(
  root: HTMLElement,
  colors: AccentColors,
  mode: AppearanceMode,
): void {
  const tokens = accentTokens(colors, mode);
  // Orange restores the original approved palette, including its separate shades.
  const original = colors[mode].toLowerCase() === ORIGINAL_ACCENT_COLORS[mode];
  for (const [key, value] of Object.entries(tokens)) {
    if (original && !key.startsWith('--cp-preview-')) root.style.removeProperty(key);
    else root.style.setProperty(key, value);
  }
}
